import {
  CompositeFilterDescriptor,
  filterBy,
  FilterDescriptor
} from '@progress/kendo-data-query';
import {
  MultiSelect,
  MultiSelectChangeEvent,
  MultiSelectFilterChangeEvent
} from '@progress/kendo-react-dropdowns';
import { GridFilterCellProps } from '@progress/kendo-react-grid';
import React, { useEffect, useState } from 'react';

interface Props {
  options: any[];
  optionField: string;
  value: any[];
  onValueChange: (value: any[]) => void;
}

export const MultiSelectFilterCell: React.FC<
  React.PropsWithChildren<Props & GridFilterCellProps>
> = props => {
  const { value, optionField, onValueChange } = props;

  const [options, setOptions] = useState<any[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<any[]>([]);

  useEffect(() => {
    const options = props.options.map(i => i[optionField]);
    setOptions(options);
    setFilteredOptions(options);
  }, [props.options, optionField]);

  const onFilterChange = (event: MultiSelectFilterChangeEvent) => {
    return setFilteredOptions(filterBy(options, event.filter));
  };

  const handleChange = (event: MultiSelectChangeEvent) => {
    onValueChange(event.value);
  };

  return (
    <div className="k-filtercell">
      <MultiSelect
        data={filteredOptions}
        onChange={handleChange}
        value={value}
        filterable={true}
        onFilterChange={onFilterChange}
      />
    </div>
  );
};

export const getMultiSelectFilterState = (
  filter: CompositeFilterDescriptor | undefined,
  field: string,
  value: string[]
): CompositeFilterDescriptor | undefined => {
  const isFilterSelected = filter && filter.filters.length > 0;
  // Return undefined when no filters are selected on both columns
  if (!isFilterSelected && value.length === 0) {
    return undefined;
  }
  let filters: Array<CompositeFilterDescriptor | FilterDescriptor> = [];
  if (value.length > 0) {
    filters.push({
      logic: 'or',
      filters: value.map(val => ({
        field,
        operator: 'eq',
        value: val
      }))
    });
  }
  if (isFilterSelected) {
    const newFilters = filter.filters.filter(f => {
      //Check if the filter has a filters array
      // ...indicating it is a CompositeFilterDescriptor
      const isComposite = (f as CompositeFilterDescriptor).filters;
      if (isComposite) {
        const compositeFilter = f as CompositeFilterDescriptor;
        // Check if any subfilters have a different field
        const hasDifferentField = compositeFilter.filters.some(
          subFilter => (subFilter as FilterDescriptor).field !== field
        );
        return hasDifferentField;
      }
      // If it's not a composite, it's a FilterDescriptor
      // ... so check its field directly
      return (f as FilterDescriptor).field !== field;
    });
    filters = filters.concat(newFilters);
  }
  if (filters.length > 0) {
    return {
      ...(filter ?? {}),
      filters,
      logic: 'and'
    };
  }
  return undefined;
};

export const extractValuesFromMultiSelectFilter = (
  filter: CompositeFilterDescriptor | undefined,
  field: string
) => {
  if (!filter || !filter.filters) return [];
  return [...(filter?.filters ?? [])]
    .filter(
      (c: CompositeFilterDescriptor | FilterDescriptor) => c && 'logic' in c
    )
    .filter((c: CompositeFilterDescriptor | FilterDescriptor) =>
      (c as CompositeFilterDescriptor).filters.some(
        (f: CompositeFilterDescriptor | FilterDescriptor) =>
          (f as FilterDescriptor).field === field
      )
    )
    .map(
      (c: CompositeFilterDescriptor | FilterDescriptor) =>
        (c as CompositeFilterDescriptor).filters
    )
    .flat()
    .map(
      (f: CompositeFilterDescriptor | FilterDescriptor) =>
        (f as FilterDescriptor).value
    );
};
